// pages/StepStatusPembayaran.tsx
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ProgressIndicator from "@/components/private/ProgressIndicator";
import StepNavigation from "@/components/private/StepNavigation";
import { PaymentSummary } from "@/components/private/PaymentSummary";
import { useUser } from "@/context/useUser";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import {
    Loader2,
    Clock,
    RefreshCcw,
    Copy,
    CheckCircle2,
    Landmark,
    Info,
    XCircle,
    Zap,
    AlertTriangle,
    Ban
} from "lucide-react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import PaymentGuideIndonesia from "@/components/public/conference-payment/PaymentGuideIndonesia";
import { PicMembersList } from "@/components/private/order/PicMembersList";
import { PaymentFeeNotice } from "@/components/private/payment/PaymentFeeNotice";

dayjs.extend(duration);

interface PaymentData {
    payment_id: number;
    registration_id: number;
    amount: number;
    status: "unpaid" | "pending" | "paid" | "expired";
    gateway_transaction_id: string;
    customer_number: string; // Internal mapping
    invoice_number: string;  // VA Number untuk BNI
    description: string;
    created_at: string;
    expired_at_payment: string;
    updated_at: string;
}

interface LaravelErrorResponse {
    message?: string;
}

export default function StepStatusPembayaran() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { data: user, isLoading: loadingUser } = useUser();
    const { data: activePeriod, isLoading: loadingPeriod } = useActivePeriod();

    const [payment, setPayment] = useState<PaymentData | null>(null);
    const [countdown, setCountdown] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [isSyncing, setIsSyncing] = useState<boolean>(false);
    const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
    const [isCancelling, setIsCancelling] = useState<boolean>(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);

    const fetchPayment = useCallback(async (): Promise<void> => {
        if (!user?.participant_id || !activePeriod?.id) return;
        try {
            const { data } = await axiosClient.get<{ data: PaymentData }>(
                `/participants/${user.participant_id}/registrations/period/${activePeriod.id}/payment`
            );
            setPayment(data.data);
            if (data.data.status === "paid") {
                queryClient.invalidateQueries({ queryKey: ["registration-step"] });
            }
        } catch (error) {
            console.error("Fetch Error:", error);
        } finally {
            setLoading(false);
        }
    }, [user?.participant_id, activePeriod?.id, queryClient]);

    const handleSyncStatus = async (silent = false): Promise<void> => {
        if (!payment?.payment_id) return;
        setIsSyncing(true);
        try {
            const { data } = await axiosClient.get<{ status: string }>(
                `/payments/sync-status/${payment.payment_id}`
            );
            if (data.status === "paid") {
                toast.success("Pembayaran Terdeteksi Lunas!");
            } else if (!silent) {
                // Auto-check di background tidak perlu toast "masih belum lunas" tiap
                // kali halaman ini dibuka - cukup tombol manual yang menampilkannya.
                toast(`Status: ${data.status.toLocaleLowerCase()}`, { icon: <Info className="w-5 h-5 text-blue-500" /> });
            }
            await fetchPayment();
        } catch (error) {
            if (silent) {
                console.error("Auto-check status pembayaran gagal:", error);
                return;
            }
            const err = error as AxiosError<LaravelErrorResponse>;
            toast.error(err.response?.data?.message || "Gagal sinkronisasi");
        } finally {
            setIsSyncing(false);
        }
    };

    // Auto-check SEKALI ke Payment Gateway saat halaman ini dibuka & tagihan masih
    // belum lunas, supaya user tidak perlu klik "Update Status" manual dulu.
    const autoCheckedPaymentIdRef = useRef<number | null>(null);
    useEffect(() => {
        if (!payment?.payment_id || payment.status === "paid") return;
        if (autoCheckedPaymentIdRef.current === payment.payment_id) return;
        autoCheckedPaymentIdRef.current = payment.payment_id;
        handleSyncStatus(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payment?.payment_id, payment?.status]);

    // Fungsi tambahan untuk menangani regenerasi tagihan
    const handleRegenerateInvoice = async () => {
        if (!payment?.registration_id) return;
        setIsRegenerating(true);
        try {
            await toast.promise(
                axiosClient.post(`/payments/registrations/${payment.registration_id}`, {
                    gateway_transaction_id: payment.gateway_transaction_id
                }),
                {
                    loading: "Memperbarui tagihan...",
                    success: "Tagihan baru berhasil dibuat!",
                    error: (err: AxiosError<LaravelErrorResponse>) =>
                        err.response?.data?.message || "Gagal memperbarui tagihan."
                }
            );
            await fetchPayment();
        } catch (error) {
            console.error(error);
        } finally {
            setIsRegenerating(false);
        }
    };

    // Batalkan tagihan yang sudah dibuat (mis. karena salah pilih paket/metode).
    // Menghit ulang Smart Payment untuk hapus tagihan di bank, lalu bersihkan data lokal.
    const handleCancelInvoice = async (): Promise<void> => {
        if (!payment?.payment_id) return;
        setIsCancelling(true);
        try {
            const { data } = await axiosClient.delete<{ message?: string }>(`/payments/${payment.payment_id}`);
            toast.success(data.message || "Tagihan berhasil dibatalkan.");
            setIsCancelModalOpen(false);
            // Tagihan sudah tidak ada: mundurkan current_step ke 3 (pembayaran) di backend
            // supaya StepGuard tidak memaksa balik ke halaman status ini, dan peserta
            // bisa lanjut mundur lagi ke Konfirmasi/Paket untuk menyesuaikan data.
            await updateStep(3);
            navigate("/order/pembayaran", { replace: true });
        } catch (error) {
            const err = error as AxiosError<LaravelErrorResponse>;
            toast.error(err.response?.data?.message || "Gagal membatalkan tagihan.");
        } finally {
            setIsCancelling(false);
        }
    };

    const copyToClipboard = (text: string): void => {
        navigator.clipboard.writeText(text);
        toast.success("Nomor VA disalin");
    };

    useEffect(() => {
        fetchPayment();
        const interval = setInterval(fetchPayment, 30000);
        return () => clearInterval(interval);
    }, [fetchPayment]);

    useEffect(() => {
        if (!payment?.expired_at_payment || payment.status === "paid") return;

        const interval = setInterval(() => {
            const now = dayjs();
            const expiration = dayjs(payment.expired_at_payment);
            const diff = expiration.diff(now);

            if (diff <= 0) {
                setCountdown("EXPIRED");
                clearInterval(interval);
            } else {
                const dur = dayjs.duration(diff);
                const days = Math.floor(dur.asDays());

                if (days > 0) {
                    setCountdown(`${days} hari ${dur.hours().toString().padStart(2, "0")}:${dur.minutes().toString().padStart(2, "0")}:${dur.seconds().toString().padStart(2, "0")}`);
                } else {
                    setCountdown(`${dur.hours().toString().padStart(2, "0")}:${dur.minutes().toString().padStart(2, "0")}:${dur.seconds().toString().padStart(2, "0")}`);
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [payment]);

    const updateStep = async (step: number): Promise<void> => {
        if (!user?.participant_id || !activePeriod?.id) return;
        await axiosClient.put(`/participants/${user.participant_id}/registrations/step/${activePeriod.id}`, { current_step: step });
        await queryClient.invalidateQueries({ queryKey: ["registration-step"] });
        await queryClient.invalidateQueries({ queryKey: ["registration-check"] });
    };

    if (loadingUser || loadingPeriod || loading) return <StatusLoadingSkeleton />;
    if (!payment) return <div className="text-center py-20 font-bold">Data pembayaran tidak ditemukan.</div>;

    const isExpired = payment.status === "expired" || countdown === "EXPIRED";

    return (
        <div className="mx-auto py-12 space-y-8 pt-0">

            {/* Header Ornamen Bali */}
            <Card className="relative border-0 shadow-lg bg-white dark:bg-gray-800 mb-6 overflow-hidden rounded-2xl">
                <img src="/ornamen/ukiran-corner.png" alt="Bali" className="absolute top-0 left-0 w-40 md:w-48 opacity-40 z-0" />
                <img src="/ornamen/ukiran-corner.png" alt="Bali" className="absolute top-0 right-0 w-40 md:w-48 scale-x-[-1] opacity-40 z-0" />
                <div className="absolute inset-0 opacity-[0.1] pointer-events-none" style={{ backgroundImage: `radial-gradient(currentColor 0.8px, transparent 0.8px)`, backgroundSize: '12px 12px' }} />

                <div className="relative z-10 px-10 py-8 flex flex-col items-center text-center space-y-2">
                    <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white uppercase">Status Pembayaran</h2>
                    <p className="text-xs font-medium text-slate-500 italic leading-relaxed">
                        Segera selesaikan pembayaran agar pendaftaran Anda dapat diproses oleh sistem.
                    </p>
                </div>
            </Card>

            <ProgressIndicator currentStep={4} />

            <div className="space-y-8">
                {/* 1. KONDISI IF EXPIRED - Tampilan Senada dan Menarik */}
                {isExpired ? (
                    <Card className="border-0 shadow-2xl bg-white dark:bg-slate-900 overflow-hidden border-t-8 border-red-500">
                        <CardContent className="p-10 text-center space-y-6">
                            <div className="mx-auto p-5 bg-red-50 dark:bg-red-900/30 rounded-full w-24 h-24 flex items-center justify-center">
                                <XCircle className="w-14 h-14 text-red-600 animate-pulse" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-2xl font-black text-red-600 dark:text-red-400 uppercase tracking-tight font-mono">Tagihan Kedaluwarsa</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto italic leading-relaxed font-medium">
                                    Batas waktu pembayaran untuk VA <strong>{payment.invoice_number}</strong> telah berakhir.
                                    Harap buat tagihan baru untuk melanjutkan pendaftaran.
                                </p>
                            </div>
                            <div className="pt-4">
                                <Button
                                    onClick={handleRegenerateInvoice}
                                    disabled={isRegenerating}
                                    className="bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest px-10 h-16 rounded-2xl shadow-xl shadow-red-200 dark:shadow-none transition-all active:scale-95 group"
                                >
                                    {isRegenerating ? (
                                        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                                    ) : (
                                        <Zap className="mr-2 h-6 w-6 fill-current group-hover:text-yellow-300 transition-colors" />
                                    )}
                                    Buat Tagihan Baru
                                </Button>
                                <div className="mt-6 flex items-center justify-center gap-2 text-red-500/70">
                                    <AlertTriangle className="w-4 h-4" />
                                    <p className="text-[10px] font-bold uppercase tracking-tighter">Batas waktu pembuatan tagihan mengikuti periode aktif sistem</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    /* 1. Kartu Nomor Virtual Account (VA) - Kondisi Normal */
                    <Card className="border-0 shadow-xl bg-white dark:bg-slate-900 rounded-[2.5rem] overflow-hidden">
                        <CardContent className="p-8 space-y-6">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-blue-600 rounded-xl shadow-lg shadow-blue-200 dark:shadow-blue-900">
                                        <Landmark className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-blue-600">Bank Negara Indonesia (BNI)</p>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase">Virtual Account Number</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {payment.status !== "paid" && (
                                        <Button variant="ghost" size="sm" onClick={() => setIsCancelModalOpen(true)} disabled={isCancelling} className="text-rose-600 font-bold text-[10px] uppercase bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/50 dark:text-rose-300">
                                            <Ban className="w-3 h-3 mr-2" />
                                            Batalkan Tagihan
                                        </Button>
                                    )}
                                    <Button variant="ghost" size="sm" onClick={() => handleSyncStatus()} disabled={isSyncing} className="text-blue-600 font-bold text-[10px] uppercase bg-blue-50 hover:bg-blue-50 dark:bg-blue-500 dark:border-slate-800 dark:hover:bg-blue-700 dark:text-white">
                                        {isSyncing ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <RefreshCcw className="w-3 h-3 mr-2" />}
                                        Update Status
                                    </Button>
                                </div>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-800/50 p-6 md:p-10 rounded-[2rem] border border-slate-100 dark:border-slate-800 flex flex-col items-center gap-6">
                                <div className="space-y-1 text-center w-full overflow-hidden">
                                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-[0.1em] md:tracking-[0.15em] text-slate-900 dark:text-white font-mono tabular-nums leading-none break-all">
                                        {payment.invoice_number}
                                    </h3>
                                </div>
                                <Button
                                    onClick={() => copyToClipboard(payment.invoice_number)}
                                    className="rounded-full bg-slate-900 dark:bg-blue-600 text-white shadow-xl font-black text-[11px] uppercase tracking-[0.2em] px-10 h-12 hover:scale-105 transition-transform w-full sm:w-auto"
                                >
                                    <Copy className="w-4 h-4 mr-2" /> Salin VA
                                </Button>
                            </div>

                            <div className="flex items-start gap-4 p-5 bg-slate-50 dark:bg-slate-800/30 rounded-2xl border border-slate-100 dark:border-slate-800">
                                <div className="p-2 bg-white dark:bg-slate-700 rounded-lg shadow-sm">
                                    <Info className="w-4 h-4 text-blue-500" />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Keterangan Tagihan</p>
                                    <p className="text-sm font-bold text-slate-700 dark:text-slate-200 leading-snug">{payment.description || "Pembayaran Konferensi"}</p>
                                </div>
                            </div>

                            {payment.status !== "paid" ? (
                                <div className="flex justify-between items-center pt-4 border-t border-slate-100 dark:border-slate-800">
                                    <div className="space-y-1">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Sisa Waktu</p>
                                        <p className="text-2xl font-black text-red-600 font-mono tracking-tighter">{countdown}</p>
                                    </div>
                                    <div className="text-right space-y-1">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">Status Bayar</p>
                                        <div className="flex items-center gap-2 text-amber-600 font-black text-sm uppercase">
                                            <Clock className="w-4 h-4 animate-pulse" /> Menunggu
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-2xl flex items-center justify-center gap-3 text-green-600 font-black uppercase tracking-[0.2em] text-sm shadow-inner">
                                    <CheckCircle2 className="w-6 h-6" /> Lunas Terkonfirmasi
                                </div>
                            )}

                            {/* Info biaya admin VA - hanya relevan selama tagihan belum lunas */}
                            {payment.status !== "paid" && (
                                <PaymentFeeNotice
                                    amount={payment.amount}
                                    isUsd={payment.gateway_transaction_id === "2"}
                                    className="pt-4 border-t border-slate-100 dark:border-slate-800"
                                />
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* 2. Kartu Ticket Summary (Bawah) */}
                <div className="pt-2">
                    <PaymentSummary
                        total={payment.amount}
                        status={payment.status}
                        method={payment.gateway_transaction_id === "2" ? "BNI Virtual Account (USD)" : "BNI Virtual Account (IDR)"}
                        isUsd={payment.gateway_transaction_id === "2"}
                        packageName={payment.description}
                        transactionId={payment.invoice_number}
                        date={dayjs(payment.created_at).format("DD MMM YYYY, HH:mm")}
                        expiryDate={payment.expired_at_payment}
                    />
                </div>

                {/* 3. Daftar Anggota (khusus PIC) - hanya tampil kalau akun ini PIC/pendaftar
                    kolektif; peserta mandiri (Pendaftar biasa) atau role lain tidak melihat ini. */}
                {user?.role_id === 3 && activePeriod?.id && (
                    <div className="pt-2">
                        <PicMembersList activePeriodId={activePeriod.id} />
                    </div>
                )}
            </div>

            <PaymentGuideIndonesia />

            <StepNavigation
                onBack={() => updateStep(3)}
                onNext={async () => {
                    if (payment.status === 'paid') await updateStep(5);
                    else toast.error("Silakan selesaikan pembayaran terlebih dahulu.");
                }}
                nextDisabled={payment.status !== "paid" || isExpired}
                backDisabled={payment.status === "paid"}
            />

            {/* Modal Konfirmasi Batalkan Tagihan */}
            {isCancelModalOpen && createPortal(
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-sm w-full p-8 text-center animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/30 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Ban className="w-8 h-8" />
                        </div>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Batalkan Tagihan?</h3>
                        <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                            Tagihan VA <strong>{payment.invoice_number}</strong> akan dibatalkan permanen di bank dan sistem.
                            Anda perlu membuat tagihan baru untuk melanjutkan pendaftaran.
                        </p>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setIsCancelModalOpen(false)} disabled={isCancelling} className="flex-1 h-12 rounded-xl font-bold">
                                Batal
                            </Button>
                            <Button onClick={handleCancelInvoice} disabled={isCancelling} className="flex-1 h-12 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold">
                                {isCancelling ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Ya, Batalkan"}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

const StatusLoadingSkeleton = () => (
    <div className="mx-auto py-12 space-y-8 pt-0 animate-pulse">
        <div className="h-40 bg-slate-200 dark:bg-slate-800 rounded-2xl" />
        <div className="h-96 bg-slate-100 dark:bg-slate-900 rounded-[2.5rem]" />
    </div>
);